import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ExternalLink, LinkedLogo, NamedLink } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import FooterDiamondSponsors from './FooterDiamondSponsors';
import FooterNewsletterSignup from './FooterNewsletterSignup';
import css from './SectionFooter.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Site footer: Console-driven brand/social/copyright + hardcoded sell CTA and legal links.
 * Always uses dark color-scheme tokens so AMS Diamond Sponsor logos (often white) stay visible.
 * Console appearance backgrounds are ignored.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance ignored — footer always uses dark theme surface tokens
 * @param {Array<BlockConfig>?} props.blocks ignored — link columns are not rendered
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for footer content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    socialMediaLinks = [],
    slogan,
    copyright,
    options,
    linkLogoToExternalSite,
  } = props;
  const intl = useIntl();

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';
  const legalNavLabel = intl.formatMessage({ id: 'Footer.legalNavLabel' });

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      options={fieldOptions}
      data-color-scheme="dark"
    >
      <div className={css.footer}>
        <div className={css.layout}>
          <div className={css.sellCta} aria-labelledby="footer-sell-cta-heading">
            <h2 id="footer-sell-cta-heading" className={css.sellHeadline}>
              <FormattedMessage id="Footer.sellHeadline" />
            </h2>
            <p className={css.sellDescription}>
              <FormattedMessage id="Footer.sellDescription" />
            </p>
            <div className={css.sellActions}>
              <NamedLink name="NewListingPage" className={css.primaryButton}>
                <FormattedMessage id="Footer.sellPrimaryCta" />
              </NamedLink>
              <ExternalLink
                href="https://www.reef2reef.com/forums/marketplace-handbook.1600/"
                className={css.secondaryButton}
              >
                <FormattedMessage id="Footer.sellSecondaryCta" />
              </ExternalLink>
            </div>
            <FooterNewsletterSignup />
          </div>

          <div className={css.brand}>
            <div className={css.brandHeader}>
              <LinkedLogo
                rootClassName={css.logoLink}
                logoClassName={css.logoWrapper}
                logoImageClassName={css.logoImage}
                linkToExternalSite={linkLogoToExternalSite}
                layout={logoLayout}
              />
              <Field data={slogan} className={css.slogan} />
            </div>
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null}
            <nav className={css.legal} aria-label={legalNavLabel}>
              <NamedLink name="TermsOfServicePage" className={css.legalLink}>
                <FormattedMessage id="Footer.termsOfService" />
              </NamedLink>
              <span className={css.legalSeparator} aria-hidden="true">
                |
              </span>
              <NamedLink name="PrivacyPolicyPage" className={css.legalLink}>
                <FormattedMessage id="Footer.privacyPolicy" />
              </NamedLink>
            </nav>
            <Field data={copyright} className={css.copyright} />
            <FooterDiamondSponsors />
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
